/**
 * IndexedDB persistence layer for Sentinel.
 *
 * Stores:
 * - Temporal baselines (analysis module state across page reloads)
 * - Playback snapshots (historical state captures)
 * - Offline event cache (events for offline/air-gap use)
 *
 * Features:
 * - Automatic schema migration
 * - LRU eviction when quota exceeded
 * - Graceful degradation if IndexedDB unavailable
 * - Typed get/put/delete API per store
 */

const DB_NAME = 'sentinel-persistence';
const DB_VERSION = 1;

/** Store names — each maps to an IndexedDB object store. */
export const STORES = {
  BASELINES: 'baselines',
  SNAPSHOTS: 'snapshots',
  EVENTS: 'offline-events',
} as const;

type StoreName = (typeof STORES)[keyof typeof STORES];

/** A playback snapshot — captures app state at a point in time. */
export interface PlaybackSnapshot {
  id: string;
  timestamp: number;
  label?: string;
  variant: string;
  timeRange: string;
  activeLayers: string[];
  mapCenter: [number, number];
  mapZoom: number;
  eventCount: number;
  /** Serialized subset of events (capped for storage efficiency). */
  events: unknown[];
}

/** A temporal baseline record. */
export interface BaselineRecord {
  key: string;
  source: string;
  type: string;
  values: number[];
  updatedAt: number;
}

/** Stored event record for offline cache. */
export interface OfflineEventRecord {
  id: string;
  data: unknown;
  storedAt: number;
}

// ── Internal helpers ──

let dbInstance: IDBDatabase | null = null;
let dbOpenPromise: Promise<IDBDatabase | null> | null = null;

/** Open the database (singleton, idempotent). */
function openDB(): Promise<IDBDatabase | null> {
  if (dbInstance) return Promise.resolve(dbInstance);
  if (dbOpenPromise) return dbOpenPromise;

  dbOpenPromise = new Promise<IDBDatabase | null>((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(STORES.BASELINES)) {
          db.createObjectStore(STORES.BASELINES, { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains(STORES.SNAPSHOTS)) {
          const store = db.createObjectStore(STORES.SNAPSHOTS, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
        if (!db.objectStoreNames.contains(STORES.EVENTS)) {
          const store = db.createObjectStore(STORES.EVENTS, { keyPath: 'id' });
          store.createIndex('storedAt', 'storedAt', { unique: false });
        }
      };

      request.onsuccess = () => {
        dbInstance = request.result;

        // Handle unexpected close
        dbInstance.onclose = () => {
          dbInstance = null;
          dbOpenPromise = null;
        };

        resolve(dbInstance);
      };

      request.onerror = () => {
        console.warn('[Sentinel] IndexedDB open failed:', request.error);
        resolve(null);
      };
    } catch (err) {
      console.warn('[Sentinel] IndexedDB not available:', err);
      resolve(null);
    }
  });

  return dbOpenPromise;
}

/** Run a read-only transaction and return the result. */
async function readTx<T>(
  storeName: StoreName,
  operation: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | undefined> {
  const db = await openDB();
  if (!db) return undefined;

  return new Promise<T | undefined>((resolve) => {
    try {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = operation(store);
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(undefined);
    } catch {
      resolve(undefined);
    }
  });
}

/** Run a read-write transaction. */
async function writeTx(
  storeName: StoreName,
  operation: (store: IDBObjectStore) => void,
): Promise<boolean> {
  const db = await openDB();
  if (!db) return false;

  return new Promise<boolean>((resolve) => {
    try {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      operation(store);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

// ── Public API: Baselines ──

/** Save a temporal baseline record. */
export async function saveBaseline(record: BaselineRecord): Promise<boolean> {
  return writeTx(STORES.BASELINES, (store) => {
    store.put(record);
  });
}

/** Get a baseline by key. */
export async function getBaseline(key: string): Promise<BaselineRecord | undefined> {
  return readTx<BaselineRecord>(STORES.BASELINES, (store) => store.get(key));
}

/** Get all baselines. */
export async function getAllBaselines(): Promise<BaselineRecord[]> {
  const result = await readTx<BaselineRecord[]>(STORES.BASELINES, (store) => store.getAll());
  return result ?? [];
}

/** Delete a baseline. */
export async function deleteBaseline(key: string): Promise<boolean> {
  return writeTx(STORES.BASELINES, (store) => {
    store.delete(key);
  });
}

// ── Public API: Snapshots ──

const MAX_SNAPSHOTS = 100;

/** Save a playback snapshot. Enforces max count via LRU eviction. */
export async function saveSnapshot(snapshot: PlaybackSnapshot): Promise<boolean> {
  const saved = await writeTx(STORES.SNAPSHOTS, (store) => {
    store.put(snapshot);
  });

  if (saved) {
    // Evict oldest if over limit
    await evictOldest(STORES.SNAPSHOTS, 'timestamp', MAX_SNAPSHOTS);
  }

  return saved;
}

/** Get a snapshot by ID. */
export async function getSnapshot(id: string): Promise<PlaybackSnapshot | undefined> {
  return readTx<PlaybackSnapshot>(STORES.SNAPSHOTS, (store) => store.get(id));
}

/** Get all snapshots, sorted newest first. */
export async function getAllSnapshots(): Promise<PlaybackSnapshot[]> {
  const result = await readTx<PlaybackSnapshot[]>(STORES.SNAPSHOTS, (store) => store.getAll());
  return (result ?? []).sort((a, b) => b.timestamp - a.timestamp);
}

/** Delete a snapshot by ID. */
export async function deleteSnapshot(id: string): Promise<boolean> {
  return writeTx(STORES.SNAPSHOTS, (store) => {
    store.delete(id);
  });
}

/** Delete all snapshots. */
export async function clearSnapshots(): Promise<boolean> {
  return writeTx(STORES.SNAPSHOTS, (store) => {
    store.clear();
  });
}

// ── Public API: Offline Events ──

const MAX_OFFLINE_EVENTS = 10_000;

/** Cache an event for offline access. */
export async function cacheEvent(record: OfflineEventRecord): Promise<boolean> {
  return writeTx(STORES.EVENTS, (store) => {
    store.put(record);
  });
}

/** Cache multiple events in a single transaction. */
export async function cacheEvents(records: OfflineEventRecord[]): Promise<boolean> {
  return writeTx(STORES.EVENTS, (store) => {
    for (const record of records) {
      store.put(record);
    }
  });
}

/** Get all cached offline events. */
export async function getCachedEvents(): Promise<OfflineEventRecord[]> {
  const result = await readTx<OfflineEventRecord[]>(STORES.EVENTS, (store) => store.getAll());
  return result ?? [];
}

/** Clear the offline event cache. */
export async function clearCachedEvents(): Promise<boolean> {
  return writeTx(STORES.EVENTS, (store) => {
    store.clear();
  });
}

/** Evict oldest offline events beyond max count. */
export async function evictOldEvents(): Promise<void> {
  await evictOldest(STORES.EVENTS, 'storedAt', MAX_OFFLINE_EVENTS);
}

// ── Utility ──

/** Evict oldest records in a store by index, keeping at most maxCount. */
async function evictOldest(
  storeName: StoreName,
  indexName: string,
  maxCount: number,
): Promise<void> {
  const db = await openDB();
  if (!db) return;

  return new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const countReq = store.count();

      countReq.onsuccess = () => {
        const count = countReq.result;
        if (count <= maxCount) {
          resolve();
          return;
        }

        // Delete oldest (count - maxCount) entries
        const deleteCount = count - maxCount;
        let deleted = 0;
        const index = store.index(indexName);
        const cursor = index.openCursor(); // ascending = oldest first

        cursor.onsuccess = () => {
          const c = cursor.result;
          if (!c || deleted >= deleteCount) {
            resolve();
            return;
          }
          c.delete();
          deleted++;
          c.continue();
        };

        cursor.onerror = () => resolve();
      };

      countReq.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

// ── Public API: Analysis Result Cache ──

const ANALYSIS_RESULT_KEY = '__analysis_result__';

/**
 * Serialized form of AnalysisResult — Maps converted to plain objects.
 * Stored in the baselines store under a reserved key.
 */
interface SerializedAnalysisResult {
  key: string;
  data: unknown;
  savedAt: number;
}

/**
 * Save the latest AnalysisResult to IndexedDB for warm-start on next session.
 * Converts Map fields to plain objects for JSON compatibility.
 * Accepts any object with ciiScores Map and optional velocity.perEntity Map.
 */
export async function saveAnalysisResult(result: Record<string, unknown>): Promise<boolean> {
  const ciiScores = result.ciiScores;
  const velocity = result.velocity as Record<string, unknown> | null;

  const serializable = {
    ...result,
    ciiScores: ciiScores instanceof Map ? Object.fromEntries(ciiScores) : ciiScores,
    velocity: velocity
      ? {
          ...velocity,
          perEntity: velocity.perEntity instanceof Map
            ? Object.fromEntries(velocity.perEntity)
            : velocity.perEntity,
        }
      : null,
  };

  return writeTx(STORES.BASELINES, (store) => {
    store.put({
      key: ANALYSIS_RESULT_KEY,
      data: serializable,
      savedAt: Date.now(),
    } satisfies SerializedAnalysisResult);
  });
}

/**
 * Restore the cached AnalysisResult from IndexedDB.
 * Converts plain objects back to Maps for ciiScores and velocity.perEntity.
 * Returns null if no cached result or if older than maxAgeMs (default 2h).
 */
export async function getAnalysisResult(
  maxAgeMs = 2 * 60 * 60 * 1000,
): Promise<{ result: unknown; savedAt: number } | null> {
  const record = await readTx<SerializedAnalysisResult>(
    STORES.BASELINES,
    (store) => store.get(ANALYSIS_RESULT_KEY),
  );
  if (!record || !record.data) return null;

  // Check staleness
  if (Date.now() - record.savedAt > maxAgeMs) return null;

  const raw = record.data as Record<string, unknown>;

  // Reconstruct Maps from plain objects
  const ciiObj = (raw.ciiScores ?? {}) as Record<string, unknown>;
  raw.ciiScores = new Map(Object.entries(ciiObj));

  if (raw.velocity && typeof raw.velocity === 'object') {
    const vel = raw.velocity as Record<string, unknown>;
    const peObj = (vel.perEntity ?? {}) as Record<string, unknown>;
    vel.perEntity = new Map(Object.entries(peObj));
  }

  return { result: raw, savedAt: record.savedAt };
}

/** Get estimated storage usage (if quota API available). */
export async function getStorageEstimate(): Promise<{ used: number; quota: number } | null> {
  if (navigator.storage?.estimate) {
    try {
      const estimate = await navigator.storage.estimate();
      return {
        used: estimate.usage ?? 0,
        quota: estimate.quota ?? 0,
      };
    } catch {
      return null;
    }
  }
  return null;
}

/** Close the database connection (for cleanup/testing). */
export function closeDB(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
    dbOpenPromise = null;
  }
}
